import { Badge, Card, Flex, Grid, Heading, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { mean } from 'lodash';
import { ReactNode } from 'react';

const StackedText = (props: { line1: ReactNode; line2: ReactNode }) => (
  <Flex direction="column">
    <Text color={RADIX.COLOR.SECONDARY}>{props.line1}</Text>
    <Text>{props.line2}</Text>
  </Flex>
);

export const TrainingPerfLogDetails = () => {
  const selectedLog = useTrainingPerformanceStore((s) => s.selectedLog);

  const totalShots =
    selectedLog?.iterations.flatMap((itr) => itr.shotIDs).length ?? 0;

  return (
    <ErrorBoundary identifier="TrainingPerfLogDetails">
      <Flex direction="column" gap="2">
        <Heading size="3">Training Details</Heading>
        <Card size="2">
          <Grid columns="3" gap="5">
            <StackedText line1="Priority" line2={selectedLog?.pitchPriority} />
            <StackedText
              line1="Mean PT Rel. Threshold"
              line2={
                selectedLog
                  ? mean(
                      selectedLog.iterations.map((itr) =>
                        TrainingHelper.getIterationPtRelThreshold({
                          priority: selectedLog.pitchPriority,
                          ball_type: selectedLog.ball_type,
                          itr: itr,
                        })
                      )
                    ).toFixed(2)
                  : undefined
              }
            />
            <StackedText
              line1="Success"
              line2={
                selectedLog?.precisionTrained ? (
                  <Badge color={RADIX.COLOR.SUCCESS}>Yes</Badge>
                ) : (
                  <Badge color={RADIX.COLOR.DANGER}>No</Badge>
                )
              }
            />
            <StackedText
              line1="Iterations"
              line2={selectedLog?.iterations.length}
            />
            <StackedText line1="Shots" line2={totalShots} />
            <StackedText
              line1="Excess Shots"
              line2={Math.max(
                0,
                totalShots - (selectedLog?.training_threshold ?? 0)
              )}
            />

            {selectedLog?.breaksTarget && (
              <>
                <StackedText
                  line1="Target Speed (mph)"
                  line2={selectedLog?.breaksTarget.vMph.toFixed(1)}
                />
                <StackedText
                  line1="Target Break (x, z - in)"
                  line2={[
                    selectedLog?.breaksTarget.breakXIn.toFixed(1),
                    selectedLog?.breaksTarget.breakZIn.toFixed(1),
                  ].join(', ')}
                />
              </>
            )}

            {selectedLog?.spinTarget && (
              <>
                <StackedText
                  line1="Target Speed (mph)"
                  line2={selectedLog?.spinTarget.vMph.toFixed(1)}
                />
                <StackedText
                  line1="Target Spin (x, y, z - rpm)"
                  line2={[
                    selectedLog?.spinTarget.wXRpm.toFixed(0),
                    selectedLog?.spinTarget.wYRpm.toFixed(0),
                    selectedLog?.spinTarget.wZRpm.toFixed(0),
                  ].join(', ')}
                />
              </>
            )}
          </Grid>
        </Card>
      </Flex>
    </ErrorBoundary>
  );
};
